import React, { FC, useCallback, useMemo } from 'react'
import { localPoint } from '@visx/event'
import { Group } from '@visx/group'
import { withParentSize, WithParentSizeProvidedProps } from '@visx/responsive'
import Pie from '@visx/shape/lib/shapes/Pie'
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip'
import _sumBy from 'lodash/sumBy'
import Box from 'transfix-ui/core/Box'
import { brandColors } from 'transfix-ui/theme/colors'
import { shadows } from 'transfix-ui/theme/shadows'
import { LegendItem } from '../LegendItem'
import DonutLoadingState from './assets/DonutLoadingState.svg'
import { LegendLabel } from './components/LegendLabel'
import { TDonutDatum, TTooltipDatum } from './types'
import { FAKE_NO_DATA } from './utils'

// Access the value for each group in the donut chart.
const getValue = (d: TDonutDatum) => d.value

const colorMapping = [brandColors.skyBlue6, brandColors.skyBlue7, brandColors.coolGray4]

// In order to use only the colors available, we select a color from the
// colorMapping list in a round robin fashion, using `% numColors` so that we
// can technically support more than three groupings in the donut.
const numColors = colorMapping.length

export interface IDonutProps {
  /**
   * An array of data points representing the total number of
   * values in each grouping. This does not have to be a percentage value, the
   * charting library will calculate the percentages and size each section of
   * the donut accordingly. The values will be summed up to determine the total
   * number that is displayed in the middle of the chart.
   *
   * When rendering an empty chart, pass in a list of data points with values
   * of 0.
   */
  data: TDonutDatum[]
  /**
   * Set to true while request to populate the data is still in flight. We'll
   * render a loading state.
   *
   * @default false
   */
  isLoading: boolean
  /**
   * Configurable thickness of the ring making up the donut
   *
   * @default 50
   */
  donutThickness?: number
  /**
   * Defines a React component that is rendered as a tooltip when hovering over
   * the chart. This should display a more detailed view of th data under the
   * cursor.
   */
  renderTooltip: (tooltipData: TTooltipDatum) => React.ReactNode
}

const TOOLTIP_WIDTH = 150

interface ResponsiveDonutProps extends WithParentSizeProvidedProps, IDonutProps {}

/**
 * Renders a donut chart for visualizing the make up of each grouping of values
 * out of a total number of values. See https://airbnb.io/visx/pies for more useage
 * examples. Currently we only support three colors, defined in `colorMapping`.
 * The groupings are sorted by size and we select a color for each grouping
 * based on the ordering of the colors in that list (i.e. the largest group is
 * rendered as skyBlue6, second largest as skyBlue7, etc).
 */
const Donut: FC<ResponsiveDonutProps> = ({
  data,
  donutThickness = 50,
  isLoading = false,
  parentWidth: width,
  renderTooltip,
}) => {
  const isAllZeros = data.every(({ value }) => value === 0)
  const height = width

  const radius = Math.min(width, height) / 2

  const sortedData = useMemo(
    () => [...data].sort((a: TDonutDatum, b: TDonutDatum) => b.value - a.value),
    [data]
  )

  const getColor = useCallback(
    (label: string): string => {
      const labelIndex = sortedData.findIndex(({ label: sortedLabel }) => sortedLabel === label)
      return colorMapping[labelIndex % numColors]
    },
    [sortedData]
  )

  const totalValues = useMemo(() => _sumBy(data, ({ value }) => value), [data])

  // set up tooltip
  const { hideTooltip, showTooltip, tooltipData, tooltipLeft = 0, tooltipTop = 0 } = useTooltip<
    TTooltipDatum
  >()

  const mouseOverHandler = (data: TTooltipDatum) => (e: React.MouseEvent) => {
    const point = localPoint(e)
    if (point !== null) {
      const { x, y } = point

      showTooltip({
        tooltipLeft: x,
        tooltipTop: y,
        tooltipData: data,
      })
    }
  }

  return (
    <Box display='flex' flexDirection='column' alignItems='center' data-test='donut'>
      {isLoading ? (
        <div style={{ width, height }}>
          <Group left={radius} top={radius}>
            <img src={DonutLoadingState} alt='Loading...' />
          </Group>
        </div>
      ) : (
        <svg width={width} height={height}>
          <Group left={radius} top={radius}>
            <Pie
              data={isAllZeros ? FAKE_NO_DATA : data}
              pieValue={getValue}
              outerRadius={radius}
              innerRadius={radius - donutThickness}
              cornerRadius={3}
              padAngle={0.005}
              fill='green'>
              {pie => {
                return pie.arcs.map(arc => {
                  const { label } = arc.data
                  const arcPath = pie.path(arc)
                  const arcFill = isAllZeros ? brandColors.coolGray1 : getColor(label)

                  return arcPath ? (
                    <g
                      onMouseLeave={hideTooltip}
                      onMouseEnter={mouseOverHandler({
                        ...arc.data,
                        color: arcFill,
                        renderedLabel: (
                          <LegendLabel
                            isLoading={false}
                            datum={arc.data}
                            totalValues={totalValues}
                          />
                        ),
                      })}
                      key={`arc-${label}`}>
                      <path d={arcPath} fill={arcFill} />
                    </g>
                  ) : null
                })
              }}
            </Pie>
            <text dominantBaseline='middle' textAnchor='middle' fontWeight={600} fontSize={34}>
              {Math.trunc(totalValues)}
            </text>
          </Group>
        </svg>
      )}

      {tooltipData && (
        <TooltipWithBounds
          style={{ ...defaultStyles, boxShadow: shadows[10], color: brandColors.coolGray8 }}
          key={tooltipData.label}
          top={tooltipTop + 24}
          // Center the tooltip with the cursor by adding the width
          left={tooltipLeft + TOOLTIP_WIDTH}>
          <Box width={TOOLTIP_WIDTH}>{renderTooltip(tooltipData)}</Box>
        </TooltipWithBounds>
      )}

      <Box width='100%' mt={3} mb={-1}>
        {sortedData.map((dataPoint, idx) => (
          <Box display='flex' flexDirection='row' mb={1} key={dataPoint.label}>
            <LegendItem
              isLoading={isLoading}
              label={
                <LegendLabel datum={dataPoint} isLoading={isLoading} totalValues={totalValues} />
              }
              color={colorMapping[idx % numColors]}
            />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default Donut

export const ResponsiveDonut: FC<IDonutProps> = withParentSize(Donut)
